import { ADEntityField } from '@/client/accounts/types/account-ad-config.type';
import { UserCompanyData } from '@/client/users';
import { LoadingStateType } from '@/common/constants';
import {
  FormikInput,
  SyncedFieldLockIcon,
  getSyncedFieldTooltip,
} from '@/components/form';
import { UsersSelectInput } from '@/components/form/selectors';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { FormContainer } from '@/ui/styled-ui';
import { isDemoRestrictedMode } from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { Field, Form, Formik, FormikValues } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { updateUserPropertiesSchema } from './validations';

const StyledManagerField = styled.div`
  label {
    display: block;
  }

  .p-tooltip-target-wrapper {
    width: 100%;
  }
`;

type UserPropertiesFormProps = {
  initialValues: UserCompanyData & { avatar?: string };
  onSubmit: (data: FormikValues) => void;
  state?: LoadingStateType;
  isDisabled?: boolean;
  isSuperAdmin?: boolean;
  syncedAdFields?: string[];
  isAdSyncedField?: boolean;
  isManagerSyncedField?: boolean;
};

export const UserPropertiesForm: React.FC<UserPropertiesFormProps> = ({
  initialValues,
  onSubmit,
  state,
  isDisabled,
  isSuperAdmin,
  syncedAdFields,
  isAdSyncedField,
  isManagerSyncedField,
}) => {
  const { t } = useTranslation();
  const { canUseFeature } = useFeatureFlag();
  const isManagerUserReferenceEnabled = canUseFeature(
    PostHogFeatureFlags.MANAGER_USER_REFERENCE,
  );
  const account = useAppSelector(selectCurrentAccount);
  const user = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, user);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={updateUserPropertiesSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, setFieldValue, setFieldTouched }) => (
        <FormContainer>
          <Form className="w-full">
            <div className="field w-full">
              <Field
                id="companyName"
                name="companyName"
                label={t('generic.companyName')}
                className="w-full"
                value={values.companyName}
                component={FormikInput}
                disabled={isDisabled}
                isSyncedField={syncedAdFields?.includes(
                  ADEntityField.COMPANY_DATA_COMPANY_NAME,
                )}
                isAdSyncedField={isAdSyncedField}
              />
            </div>
            <div className="field w-full">
              <Field
                id="department"
                name="department"
                label={t('generic.department')}
                className="w-full"
                value={values.department}
                component={FormikInput}
                disabled={isDisabled}
                isSyncedField={syncedAdFields?.includes(
                  ADEntityField.COMPANY_DATA_DEPARTMENT,
                )}
                isAdSyncedField={isAdSyncedField}
              />
            </div>
            {isManagerUserReferenceEnabled ? (
              <StyledManagerField className="field w-full">
                <label htmlFor="managerUser">
                  {isManagerSyncedField && <SyncedFieldLockIcon />}
                  {t('generic.manager')}
                </label>
                <UsersSelectInput
                  inputId="managerUser"
                  className="w-full"
                  placeholder={t('manager.select')}
                  selectedValue={values.managerUser}
                  onChange={(value) => setFieldValue('managerUser', value)}
                  onBlur={() => setFieldTouched('managerUser', true)}
                  isDisabled={isDisabled || isManagerSyncedField}
                  tooltip={getSyncedFieldTooltip(
                    undefined,
                    isManagerSyncedField,
                    isAdSyncedField,
                    t,
                  )}
                />
              </StyledManagerField>
            ) : (
              <>
                <div className="field w-full">
                  <Field
                    id="manager"
                    name="manager"
                    label={t('generic.manager')}
                    className="w-full"
                    value={values.manager}
                    component={FormikInput}
                    disabled={isDisabled}
                    isSyncedField={syncedAdFields?.includes(
                      ADEntityField.COMPANY_DATA_MANAGER,
                    )}
                    isAdSyncedField={isAdSyncedField}
                  />
                </div>
                <div className="field w-full">
                  <Field
                    id="managerEmail"
                    name="managerEmail"
                    label={t('generic.managerEmail')}
                    className="w-full"
                    value={values.managerEmail}
                    component={FormikInput}
                    disabled={isDisabled}
                    isSyncedField={syncedAdFields?.includes(
                      ADEntityField.COMPANY_DATA_MANAGER,
                    )}
                    isAdSyncedField={isAdSyncedField}
                  />
                </div>
              </>
            )}
            <div className="field w-full">
              <Field
                id="country"
                name="country"
                label={t('generic.country')}
                className="w-full"
                value={values.country}
                component={FormikInput}
                disabled={isDisabled}
                isSyncedField={syncedAdFields?.includes(
                  ADEntityField.COMPANY_DATA_COUNTRY,
                )}
                isAdSyncedField={isAdSyncedField}
              />
            </div>
            <div className="field w-full">
              <Field
                id="jobTitle"
                name="jobTitle"
                label={t('generic.jobTitle')}
                className="w-full"
                value={values.jobTitle}
                component={FormikInput}
                disabled={isDisabled}
                isSyncedField={syncedAdFields?.includes(
                  ADEntityField.COMPANY_DATA_JOB_TITLE,
                )}
                isAdSyncedField={isAdSyncedField}
              />
            </div>
            <div className="field w-full">
              <Field
                id="mobilePhone"
                name="mobilePhone"
                label={t('generic.mobilePhone')}
                className="w-full"
                value={values.mobilePhone}
                component={FormikInput}
                disabled={isDisabled}
                isSyncedField={syncedAdFields?.includes(
                  ADEntityField.COMPANY_DATA_MOBILE_PHONE,
                )}
                isAdSyncedField={isAdSyncedField}
              />
            </div>
            <div className="field w-full">
              <Field
                id="officeLocation"
                name="officeLocation"
                label={t('generic.officeLocation')}
                className="w-full"
                value={values.officeLocation}
                component={FormikInput}
                disabled={isDisabled}
                isSyncedField={syncedAdFields?.includes(
                  ADEntityField.COMPANY_DATA_OFFICE_LOCATION,
                )}
                isAdSyncedField={isAdSyncedField}
              />
            </div>
            {account?.isSystem && (
              <>
                <div className="field w-full">
                  <Field
                    id="avatar"
                    name="avatar"
                    label={t('generic.imageUrl')}
                    className="w-full"
                    value={values.avatar}
                    component={FormikInput}
                    disabled={isDisabled}
                  />
                </div>
                {isSuperAdmin && (
                  <div className="field w-full mb-4">
                    <Field
                      id="hubspotMeetingLink"
                      name="hubspotMeetingLink"
                      label={t('hubspot.meetingLink')}
                      className="w-full"
                      component={FormikInput}
                      disabled={isDisabled}
                    />
                  </div>
                )}
              </>
            )}

            <AppButton
              isSubmit={!isDemoAccount}
              severity="secondary"
              label={t('button.save')}
              className="w-4 mt-4 mb-5"
              state={state}
              isDisabled={!!Object.keys(errors).length || isDisabled}
            />
          </Form>
        </FormContainer>
      )}
    </Formik>
  );
};
